import type { MaybeRefOrGetter } from 'vue';

import type { ActivityOrchestratorContext } from '~/composables/unified/useActivityOrchestrator';
import type { LearningAssetEventProps } from '~/composables/unified/useLearningAssetFlow';
import type { AnalyticsOrigin } from '~/constants/analyticsOrigin';
import type { ArmOf } from '~/utils/experiments/defineExperiment';

import { useSaveProgressRedirect } from '~/composables/unified/useSaveProgressRedirect';
import { useExperiment } from '~/composables/useExperiment';
import { EVENTS } from '~/constants/events';
import { GHOST_COMPLETION_EXPERIMENT } from '~/constants/experiments';
import { UNIFIED_ROUTE_LEARN } from '~/constants/routes/unified';
import { UnifiedSavedEntityType } from '~/graphql/generated/graphql';

/** The `cta` value a press reports. */
export const GhostCompletionCta = {
  SaveProgress: 'save_progress',
  KeepLearning: 'keep_learning',
} as const;
export type GhostCompletionCtaValue = (typeof GhostCompletionCta)[keyof typeof GhostCompletionCta];

/** The `position` value a press reports: which of the two buttons it was. */
export const CtaPosition = {
  Primary: 'primary',
  Secondary: 'secondary',
} as const;
export type CtaPositionValue = (typeof CtaPosition)[keyof typeof CtaPosition];

type GhostCompletionArm = ArmOf<typeof GHOST_COMPLETION_EXPERIMENT>;

/**
 * What each arm of the keep-learning experiment puts on a ghost's buttons. Control is today's
 * screen, save progress and nothing else; the other arms add keep learning, in second place or
 * in first.
 */
const GHOST_COMPLETION_CTAS: Record<GhostCompletionArm, { primary: GhostCompletionCtaValue; secondary: GhostCompletionCtaValue | null }> = {
  'control': { primary: GhostCompletionCta.SaveProgress, secondary: null },
  'save-primary': { primary: GhostCompletionCta.SaveProgress, secondary: GhostCompletionCta.KeepLearning },
  'learn-primary': { primary: GhostCompletionCta.KeepLearning, secondary: GhostCompletionCta.SaveProgress },
};

export interface GhostCompletionContext {
  asset: { id: string; name: string } | null;
  /** The asset's analytics identity, as the flow stamps it on every asset event. */
  assetEventProps: LearningAssetEventProps;
  collectionId?: string;
  collectionName?: string;
  collectionSlug?: string;
  origin?: AnalyticsOrigin;
}

/**
 * The buttons a ghost gets at the end of a learning asset, per arm of the keep-learning
 * experiment (CP-11944), and where each one goes. Accounts are untouched: no labels, and continue
 * hands straight back to the flow. The experiment is this file plus two KUI props, so ending it
 * means hard-coding the winning arm here and then deleting it.
 */
export const useGhostCompletionCtas = (
  source: MaybeRefOrGetter<GhostCompletionContext>,
  { previewMode, orchestrator, continueFlow }: {
    previewMode: boolean;
    orchestrator: Pick<ActivityOrchestratorContext, 'navigateAway' | 'cancelFlow' | 'collectionCompletion' | 'isLastRemainingAsset'>;
    /** What finishing does for an account, and for a ghost keeping learning inside a collection. */
    continueFlow: () => void;
  },
) => {
  const { t } = useI18n();
  const { isConsumerGhost } = useAuth();
  const route = useRoute();
  const analytics = useAnalyticsService();
  const { resolveSaveProgressRoute } = useSaveProgressRedirect();
  const experiment = useExperiment(GHOST_COMPLETION_EXPERIMENT);

  const context = computed(() => toValue(source));
  const isInsideCollection = computed(() => !!context.value.collectionId);
  // Asked of the collection rather than read off the completion it reports, because that report
  // only arrives when saving the progress succeeds.
  const hasNextAsset = computed(() => {
    if (!isInsideCollection.value) {
      return false;
    }
    const assetId = context.value.assetEventProps.assetId;
    if (orchestrator.isLastRemainingAsset && assetId) {
      return !orchestrator.isLastRemainingAsset(assetId);
    }

    return !orchestrator.collectionCompletion?.value?.isFullyCompleted;
  });

  // A ghost is enrolled when the buttons first come on screen, so the exposure counts only ghosts
  // who saw their arm. Accounts and teacher previews are never enrolled.
  const handleCtaShown = () => {
    if (!previewMode && isConsumerGhost.value) {
      void experiment.enrol();
    }
  };

  const ctas = computed(() => GHOST_COMPLETION_CTAS[experiment.arm.value]);
  const labelFor = (cta: GhostCompletionCtaValue) => (cta === GhostCompletionCta.KeepLearning
    ? t('unified.activity.completion.keepLearning')
    : t('unified.activity.completion.saveProgress'));
  const primaryLabel = computed(() => (isConsumerGhost.value ? labelFor(ctas.value.primary) : null));
  const secondaryLabel = computed(() => (
    isConsumerGhost.value && ctas.value.secondary ? labelFor(ctas.value.secondary) : null
  ));

  const busy = ref<CtaPositionValue | null>(null);
  const primaryLoading = computed(() => busy.value === CtaPosition.Primary);
  const secondaryLoading = computed(() => busy.value === CtaPosition.Secondary);

  const saveProgress = async (position: CtaPositionValue) => {
    busy.value = position;
    // Inside a collection, return the user to the collection after onboarding; a standalone
    // asset has no slug to return to, so onboarding falls through to the dashboard.
    const destination = await resolveSaveProgressRoute(
      isInsideCollection.value
        ? {
            entityType: UnifiedSavedEntityType.LearningAssetCollection,
            entityId: context.value.collectionId ?? '',
            entityName: context.value.collectionName ?? '',
            entitySlug: context.value.collectionSlug,
          }
        : {
            entityType: UnifiedSavedEntityType.LearningAsset,
            entityId: context.value.asset?.id ?? '',
            entityName: context.value.asset?.name ?? '',
          },
    );
    orchestrator.navigateAway(destination);
  };

  // The way on without signing in. With another asset ahead the flow carries the ghost to it as
  // it would an account. After the last one, or on a standalone asset, the flow would end on the
  // login wall, so the ghost goes back to the page that opened the asset when there is one, and
  // otherwise to Learn, where there is more to do.
  const keepLearning = (position: CtaPositionValue) => {
    busy.value = position;
    if (hasNextAsset.value) {
      continueFlow();

      return;
    }
    if (!isInsideCollection.value && route.query.from) {
      orchestrator.cancelFlow();

      return;
    }
    void orchestrator.navigateAway(UNIFIED_ROUTE_LEARN);
  };

  const run = async (cta: GhostCompletionCtaValue, position: CtaPositionValue) => {
    analytics.track(EVENTS.UNIFIED_CONTENT_ASSET_COMPLETION_CTA_CLICKED, {
      cta,
      position,
      ...context.value.assetEventProps,
      collectionId: context.value.collectionId ?? null,
      collectionName: context.value.collectionName ?? null,
      collectionSlug: context.value.collectionSlug ?? null,
      origin: context.value.origin ?? null,
      ...experiment.experimentProps(),
    });
    if (cta === GhostCompletionCta.SaveProgress) {
      await saveProgress(position);

      return;
    }
    keepLearning(position);
  };

  const handleContinue = async () => {
    if (isConsumerGhost.value) {
      await run(ctas.value.primary, CtaPosition.Primary);

      return;
    }
    continueFlow();
  };

  const handleSecondary = async () => {
    const cta = ctas.value.secondary;
    if (isConsumerGhost.value && cta) {
      await run(cta, CtaPosition.Secondary);
    }
  };

  return { primaryLabel, secondaryLabel, primaryLoading, secondaryLoading, handleContinue, handleSecondary, handleCtaShown };
};
